import { useCallback, useMemo, useState } from 'react';
const ALL_STEPS = [
    'intro',
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'secureLogin',
    'integrations',
    'qboXeroIntegration',
    'waiting',
    'closing'
];
/**
 * Resolve the V2 onboarding mode for the user clicking "Create vendor account".
 *
 * `isClient` reads the active worksAt's party type. Clicking the CTA calls
 * setWorksAt() and may swap the JWT, which can flip data.type from Customer to
 * Vendor before HomePage's effect re-runs — losing the `isClient` signal. Using
 * `hasCustomerWorkspace` (a property of the user's workspace list, not their
 * active context) restores it: a user who has at least one Customer workspace
 * is — by definition — coming from a client surface, regardless of which
 * business they are momentarily switched to.
 */ export const deriveVobMode = (param)=>{
    let { sp_onboarding_phase_1, isClient, hasCustomerWorkspace, hasCompletedVendorWorkspace } = param;
    if (!sp_onboarding_phase_1) return 'default';
    // hasCompletedVendorWorkspace must outrank clientToVendor: a user who already onboarded a vendor still has their original customer workspace forever, so this is now an additional account, not a first-time client→vendor flow.
    if (hasCompletedVendorWorkspace) return 'additionalAccount';
    if (isClient || hasCustomerWorkspace) return 'clientToVendor';
    return 'default';
};
const DEFAULT_STEPS = [
    'intro',
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'integrations',
    'qboXeroIntegration',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'waiting',
    'closing'
];
const ADDITIONAL_ACCOUNT_STEPS = DEFAULT_STEPS.filter((step)=>step !== 'intro' && step !== 'intent' && step !== 'waiting');
const CLIENT_TO_VENDOR_STEPS = (()=>{
    const brandIdx = DEFAULT_STEPS.indexOf('brand');
    return [
        ...DEFAULT_STEPS.slice(0, brandIdx + 1),
        'secureLogin',
        ...DEFAULT_STEPS.slice(brandIdx + 1)
    ];
})();
const STEPS_BY_MODE = {
    default: DEFAULT_STEPS,
    additionalAccount: ADDITIONAL_ACCOUNT_STEPS,
    clientToVendor: CLIENT_TO_VENDOR_STEPS
};
const DATA_COLLECTION_STEPS = new Set([
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'secureLogin',
    'integrations',
    'qboXeroIntegration'
]);
const SKIPPABLE_STEPS = new Set([
    'intent',
    'integrations',
    'inviteTeam',
    'businessWebsite',
    'brand'
]);
const PROGRESS_STORAGE_KEY = 'anchor.vendorOnboarding.progress';
const LOGO_STORAGE_KEY = 'anchor.vendorOnboarding.logo';
export const readPersistedProgress = ()=>{
    try {
        const value = sessionStorage.getItem(PROGRESS_STORAGE_KEY);
        if (value == null) return null;
        return JSON.parse(value);
    } catch (unused) {
        return null;
    }
};
export const persistProgress = (progress)=>{
    try {
        sessionStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
    } catch (unused) {
    // sessionStorage may be unavailable or quota-exceeded — fail silently
    }
};
export const readPersistedLogo = ()=>{
    try {
        return sessionStorage.getItem(LOGO_STORAGE_KEY);
    } catch (unused) {
        return null;
    }
};
export const persistLogo = (logo)=>{
    try {
        if (logo != null) {
            sessionStorage.setItem(LOGO_STORAGE_KEY, logo);
        } else {
            sessionStorage.removeItem(LOGO_STORAGE_KEY);
        }
    } catch (unused) {
    // sessionStorage may be unavailable or quota-exceeded — fail silently
    }
};
export const readPersistedCreditsAwarded = ()=>{
    var _ref;
    var _readPersistedProgress;
    return (_ref = (_readPersistedProgress = readPersistedProgress()) === null || _readPersistedProgress === void 0 ? void 0 : _readPersistedProgress.creditsAwarded) !== null && _ref !== void 0 ? _ref : false;
};
export const readPersistedCurrentStep = ()=>{
    var _readPersistedProgress;
    const step = (_readPersistedProgress = readPersistedProgress()) === null || _readPersistedProgress === void 0 ? void 0 : _readPersistedProgress.currentStep;
    if (step == null) return null;
    return ALL_STEPS.includes(step) ? step : null;
};
export const shouldResumeVendorOnboarding = (param)=>{
    let { sp_onboarding_phase_1, persistedStep } = param;
    if (!sp_onboarding_phase_1) return false;
    return persistedStep != null;
};
export const clearPersistedOnboardingProgress = ()=>{
    try {
        sessionStorage.removeItem(PROGRESS_STORAGE_KEY);
        sessionStorage.removeItem(LOGO_STORAGE_KEY);
    } catch (unused) {
    // sessionStorage may be unavailable — fail silently
    }
};
export const useOnboardingFlow = (options)=>{
    var _ref;
    const mode = (_ref = options === null || options === void 0 ? void 0 : options.mode) !== null && _ref !== void 0 ? _ref : 'default';
    const baseSteps = STEPS_BY_MODE[mode];
    const activeSteps = useMemo(()=>{
        return baseSteps.filter((step)=>{
            if ((options === null || options === void 0 ? void 0 : options.skipInviteTeam) && step === 'inviteTeam') {
                return false;
            }
            if (((options === null || options === void 0 ? void 0 : options.skipQboXero) || (options === null || options === void 0 ? void 0 : options.isMobile)) && step === 'qboXeroIntegration') {
                return false;
            }
            if ((options === null || options === void 0 ? void 0 : options.skipSecureLogin) && step === 'secureLogin') {
                return false;
            }
            return true;
        });
    }, [
        baseSteps,
        options === null || options === void 0 ? void 0 : options.skipInviteTeam,
        options === null || options === void 0 ? void 0 : options.skipQboXero,
        options === null || options === void 0 ? void 0 : options.skipSecureLogin,
        options === null || options === void 0 ? void 0 : options.isMobile
    ]);
    const [stepIndex, setStepIndex] = useState(()=>{
        const persistedStep = readPersistedCurrentStep();
        if (persistedStep != null) {
            const persistedIdx = activeSteps.indexOf(persistedStep);
            if (persistedIdx >= 0) return persistedIdx;
            // persistedStep is in this mode's flow but the activeSteps filter dropped
            // it after the user reached it (e.g., 'secureLogin' filtered once
            // hasPassword flipped to true). Resume at the next active step instead of
            // falling through to the credits-awarded businessDetailsA bounce — ANCR-1412.
            const persistedBaseIdx = baseSteps.indexOf(persistedStep);
            if (persistedBaseIdx >= 0) {
                for(let i = persistedBaseIdx + 1; i < baseSteps.length; i++){
                    const nextActiveIdx = activeSteps.indexOf(baseSteps[i]);
                    if (nextActiveIdx >= 0) return nextActiveIdx;
                }
            }
        }
        if (!readPersistedCreditsAwarded()) return 0;
        const idx = activeSteps.indexOf('businessDetailsA');
        return idx >= 0 ? idx : 0;
    });
    const clampedIndex = Math.min(stepIndex, activeSteps.length - 1);
    const currentStep = activeSteps[clampedIndex];
    // Progress denominator: count every data-collection step in this mode's base list, regardless
    // of which ones are currently filtered out for navigation. inviteTeam (skipInviteTeam) and
    // qboXeroIntegration (skipQboXero) still occupy a slot on the bar so it does not jump or
    // shrink mid-flow.
    const progressSteps = useMemo(()=>baseSteps.filter((step)=>DATA_COLLECTION_STEPS.has(step)), [
        baseSteps
    ]);
    const totalDataCollectionSteps = progressSteps.length;
    const dataCollectionIndex = progressSteps.indexOf(currentStep);
    const isDataCollectionStep = dataCollectionIndex >= 0;
    const isSkippableStep = SKIPPABLE_STEPS.has(currentStep);
    const prevStep = clampedIndex > 0 ? activeSteps[clampedIndex - 1] : null;
    const isBackDisabled = clampedIndex === 0 || prevStep === 'businessDetailsB';
    const progress = isDataCollectionStep ? (dataCollectionIndex + 1) / (totalDataCollectionSteps + 1) * 100 : 0;
    const goNext = useCallback(()=>{
        setStepIndex((prev)=>Math.min(prev + 1, activeSteps.length - 1));
    }, [
        activeSteps.length
    ]);
    const goBack = useCallback(()=>{
        setStepIndex((prev)=>Math.max(prev - 1, 0));
    }, []);
    return {
        currentStep,
        progress,
        isDataCollectionStep,
        isSkippableStep,
        isBackDisabled,
        goNext,
        goBack
    };
};
